import React from 'react';
import styled, { css } from 'styled-components';
import { COLORS } from '../styles/index';
import { Search as SearchIcon, Clear as ClearIcon, Attention } from '../icons';
import { WarningMessage, activeValidationMessageState, InputWrapper, tabbedHereStyle } from './styling';

const Search = styled.input.attrs(() => ({ type: 'search' }))`
  appearance: none;
  font-size: 1rem;
  width: calc(100% - 96px);

  /* To prevent browser putting its own cross inside the search bar */
  ::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }
  ::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }
  ::-webkit-search-decoration,
  ::-webkit-search-cancel-button,
  ::-webkit-search-results-button,
  ::-webkit-search-results-decoration {
    display: none;
  }

  ::placeholder {
    font-style: italic;
    color: ${COLORS.neutral_400};
  }

  &:focus {
    outline-width: 0;
  }
`;

const BigSearch = styled(Search)`
  height: 48px;
  font-size: 1.125rem;
  border-width: 0;
  padding: 0 16px;
  margin-left: 36px;
  color: ${COLORS.black};

  ::placeholder {
    color: ${COLORS.neutral_600};
  }

  &:disabled {
    background: transparent;
    border: none;
  }
`;

const DisabledState = css`
  border: 1px solid ${COLORS.neutral_100};
  pointer-events: none;
  background-color: transparent !important;
  box-shadow: none !important;
`;

const StyledSearchField = styled.div<{ disabled?: boolean; activeValidationMessage?: boolean; tabbedHere?: boolean }>`
  display: flex;
  flex-direction: row;
  border: solid 2px transparent;
  box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_300};
  box-sizing: border-box;
  border-radius: 56px;

  align-items: center;
  position: relative;
  height: 56px;

  appearance: none;
  font-size: 1rem;
  width: 100%;

  ::placeholder {
    font-style: italic;
    color: ${COLORS.neutral_400};
  }

  &:hover {
    -webkit-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_700};
    -moz-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_700};
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_700};
    border-color: transparent;
  }

  &:focus-within {
    -webkit-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
    -moz-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
    border-color: transparent;
  }

  &:disabled {
    border: 1px solid ${COLORS.neutral_100};
    pointer-events: none;
    background-color: transparent !important;
  }

  div {
    color: ${COLORS.neutral_600};
  }

  div:focus {
    outline: none;
    box-shadow: 0 0 8px 0 ${COLORS.toast_info};
  }

  div:hover {
    background: ${COLORS.primary_20};
    color: ${COLORS.primary_800};
    cursor: pointer;
  }

  div:active {
    background: ${COLORS.primary_100};
    color: ${COLORS.primary_800};
  }

  ${props => (props.disabled ? DisabledState : '')}
  ${props => (props.activeValidationMessage ? activeValidationMessageState : '')}
  ${props => (props.tabbedHere ? tabbedHereStyle : '')}
`;

const StyledIcon = styled.div`
  padding: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin: 6px;
  margin-left: 10px;
`;

const SearchIconWrapper = styled(StyledIcon)`
  position: absolute;
  top: 3px;
  left: 0;
`;

type SeachBarProps = {
  id: string;
  searchTerm?: string;
  setSearchTerm: (term: string) => void;
  enterSearch: (e: any) => void;
  removeSearch: (e: any) => void;
  placeholder?: string;
  performSearchLabel?: string;
  disabled?: boolean;
  validationMessage?: string;
};

const SearchBar: React.FunctionComponent<SeachBarProps> = ({
  id,
  searchTerm,
  setSearchTerm,
  enterSearch,
  removeSearch,
  placeholder,
  performSearchLabel,
  disabled,
  validationMessage,
}) => {
  const inputRef = React.useRef<any>(null);
  const [tabbedHere, setTabbedHere] = React.useState<boolean>(false);
  return (
    <>
      <InputWrapper
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onFocus={(e: any) => {
          if (!e?.target?.id.includes(id) && !disabled) {
            setTabbedHere(true);
            inputRef.current.focus();
          }
        }}>
        <StyledSearchField
          id={`${id}_main`}
          className={searchTerm !== '' ? 'searchactive' : ''}
          disabled={disabled}
          tabbedHere={tabbedHere}
          tabIndex={-1}
          activeValidationMessage={validationMessage !== undefined && validationMessage !== ''}>
          <BigSearch
            placeholder={placeholder}
            id={id}
            tabIndex={-1}
            disabled={disabled}
            onChange={(e: any) => setSearchTerm(e.target.value)}
            value={searchTerm}
            onKeyPress={enterSearch}
            ref={inputRef}
            onBlur={() => setTabbedHere(false)}
          />
          <SearchIconWrapper id={`${id}_Search`} onClick={enterSearch} onKeyPress={enterSearch} role="button" aria-label={performSearchLabel} tabIndex={disabled ? -1 : 0}>
            <SearchIcon size="24px" />
          </SearchIconWrapper>
          <StyledIcon
            id={`${id}_Clear`}
            role="button"
            onKeyPress={removeSearch}
            onClick={removeSearch}
            hidden={searchTerm === '' || searchTerm === undefined}
            tabIndex={disabled ? -1 : 0}>
            <ClearIcon size="24px" />
          </StyledIcon>
        </StyledSearchField>
      </InputWrapper>
      {validationMessage && (
        <WarningMessage>
          <Attention size="20px" color={COLORS.warning_400} />
          <span>{validationMessage}</span>
        </WarningMessage>
      )}
    </>
  );
};

export default SearchBar;
